import { useState, useEffect, useCallback, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext, User } from "./context";
import { SignInResponse } from "../BrandAuthContext/context";
import toast from "react-hot-toast";
import { BrandUser } from "@/types/brand";
import { brandTourManager } from "@/utils/brandTourManager";
import { tourManager } from "@/utils/tourManager";
import { BrandContext } from "../BrandContext/context";
import { clearLocalStorageExcept } from "@/utils/localStorageUtils";
import { clearOnboardingSkip } from "@/lib/brandOnboarding";
import { destroyJivoChat } from "@/utils/jivoChat";
import { resetBrandTheme } from "@/theme/brandTheme";

interface LoginData {
  user: User;
  token: string;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | BrandUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState<string | null>(
    localStorage.getItem("brand_token")
  );
  const [version, setVersion] = useState(0); // Add version state for forcing re-renders
  const { brandDetails, fetchBrandDetails } = useContext(BrandContext);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Check for stored user data on mount
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      // Also check for brand_user if no regular user found
      const brandUser = localStorage.getItem("brand_user");
      if (brandUser) {
        try {
          setUser(JSON.parse(brandUser));
        } catch (e) {
          console.error("Error parsing brand_user:", e);
        }
      }
    }
    setIsLoading(false);
  }, []);

  const login = (data: LoginData) => {
    setUser(data.user);
    clearLocalStorageExcept("affiliate");
    localStorage.setItem("user", JSON.stringify(data.user));
    localStorage.setItem("token", data.token);

    // Handle tour initialization for both brand and affiliate users
    const createdAt = data.user.createdAt || new Date().toISOString();

    // Check if user is logging in within first 24 hours of signup
    const isWithin24Hours =
      new Date().getTime() - new Date(createdAt).getTime() <
      24 * 60 * 60 * 1000;

    // Check if createdAt is not 14 days old (tour should still be available)
    const isWithin14Days =
      new Date().getTime() - new Date(createdAt).getTime() <
      14 * 24 * 60 * 60 * 1000;

    if (data.user.role === "BRAND") {
      const tourData = brandTourManager.getTourData();

      // Initialize tour data if:
      // 1. No existing tour data AND user is within 24 hours of signup
      // 2. OR user is within 14 days and no tour data exists
      if ((!tourData && isWithin24Hours) || (!tourData && isWithin14Days)) {
        brandTourManager.initializeTourData();
      }
    } else if (data.user.role === "AFFILIATE") {
      const tourData = tourManager.getTourData();

      // Initialize tour data if:
      // 1. No existing tour data AND user is within 24 hours of signup
      // 2. OR user is within 14 days and no tour data exists
      if ((!tourData && isWithin24Hours) || (!tourData && isWithin14Days)) {
        tourManager.initializeTourData();
      }
    }
  };

  const brandLogin = useCallback(async (data: SignInResponse) => {
    const { access_token, brand } = data;
    clearLocalStorageExcept("brand");
    // A fresh login starts a fresh session: forget any earlier "skip to
    // dashboard" choice so incomplete setups land on onboarding again.
    clearOnboardingSkip();
    // Drop any cached data from a previously logged-in account so the new
    // account never briefly sees the previous user's queries (e.g. AI Search
    // conversations/credits) before its own data loads.
    queryClient.clear();
    localStorage.setItem("brand_token", access_token);
    const user = {
      id: brand.id,
      name: `${brand.firstName} ${brand.lastName}`,
      email: brand.email,
      shop: brand.shop,
      profileImage: brand.profileImage || null,
      role: "BRAND" as const,
      companyName: null, // Initially null, will be updated when brand details are fetched
    };
    setUser(user);
    localStorage.setItem("brand_user", JSON.stringify(user));
    setAuthToken(access_token);
    setVersion((v) => v + 1); // Increment version on login

    // Fetch brand details to get createdAt for tour logic
    try {
      if (!brandDetails) {
        await fetchBrandDetails();
      }
      const tourData = brandTourManager.getTourData();

      // Use brandDetails.createdAt or fallback to current date
      const createdAt = brandDetails?.createdAt || new Date().toISOString();

      // Check if user is logging in within first 24 hours of signup
      const isWithin24Hours =
        new Date().getTime() - new Date(createdAt).getTime() <
        24 * 60 * 60 * 1000;

      // Check if createdAt is not 14 days old (tour should still be available)
      const isWithin14Days =
        new Date().getTime() - new Date(createdAt).getTime() <
        14 * 24 * 60 * 60 * 1000;

      // Initialize tour data if:
      // 1. No existing tour data AND user is within 24 hours of signup
      // 2. OR user is within 14 days and no tour data exists
      if ((!tourData && isWithin24Hours) || (!tourData && isWithin14Days)) {
        brandTourManager.initializeTourData();
      }
    } catch (error) {
      console.error(
        "Error fetching brand details for tour initialization:",
        error
      );
      // Fallback: initialize tour data if no existing tour data
      const tourData = brandTourManager.getTourData();
      if (!tourData) {
        brandTourManager.initializeTourData();
      }
    }
  }, [queryClient]);

  const brandLogout = useCallback(() => {
    setUser(null);
    setAuthToken(null);
    localStorage.removeItem("brand_user");
    localStorage.removeItem("brand_token");
    localStorage.removeItem("brand_temp_token");
    localStorage.removeItem("brand_refresh_token");
    localStorage.removeItem("reveshare_shop_detail");
    localStorage.removeItem("shop_details");
    localStorage.removeItem("brand_shop_detail");
    localStorage.removeItem("brand_shop");
    // Purge cached queries (AI Search chats/credits, etc.) so a different
    // account signing in afterwards can't see the previous user's data.
    queryClient.clear();
    setVersion((v) => v + 1); // Increment version on logout
    destroyJivoChat(); // Clean up JivoChat widget
    resetBrandTheme(); // Drop tenant colors so the login screen shows defaults
    toast.success("Logged out successfully");
  }, [queryClient]);

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    queryClient.clear();
    destroyJivoChat(); // Clean up JivoChat widget
    resetBrandTheme();
  };

  // Sync with brand_user changes from BrandAuthContext
  useEffect(() => {
    const handleStorageChange = () => {
      const brandUser = localStorage.getItem("brand_user");
      if (brandUser) {
        try {
          const parsedUser = JSON.parse(brandUser);
          // Update if: no current user, or current user is a brand user (to handle updates)
          if (!user || user.role === "BRAND") {
            setUser(parsedUser);
          }
        } catch (e) {
          console.error("Error parsing brand_user:", e);
        }
      } else if (user?.role === "BRAND") {
        // If brand_user was removed and current user is a brand, clear user
        setUser(null);
      }
    };

    // Listen for storage changes (cross-tab)
    window.addEventListener("storage", handleStorageChange);

    // Listen for custom event for same-tab updates (dispatched after signup)
    window.addEventListener("brand_token_updated", handleStorageChange);

    // Listen for BrandAuthProvider successfully fetching/clearing the brand user
    window.addEventListener("brand_user_updated", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("brand_token_updated", handleStorageChange);
      window.removeEventListener("brand_user_updated", handleStorageChange);
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        brandAuth: {
          user: user as BrandUser,
          isAuthenticated: !!user,
          isLoading,
          login: brandLogin,
          logout: brandLogout,
          version,
          authToken,
        },
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        authToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
